import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { setError } from '../../store/slices/authSlice';
import { testFirebaseAuth, testFirestoreWrite } from '../../utils/testFirebase';

// Helper function to get user-friendly error messages
const getErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'Invalid email address format.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'Account not found. Please check your email or sign up.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed login attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';
    case 'auth/configuration-not-found':
      return 'Authentication service is not configured correctly. Please contact support.';
    default:
      return 'An error occurred during login. Please try again.';
  }
};

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isAuthenticated, loading: authLoading, error, user } = useSelector((state: RootState) => state.auth);

  // Get the return URL from query params
  const returnTo = searchParams.get('returnTo') || '/';

  // Effect to navigate when auth state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(returnTo, { replace: true });
      toast.success(`Welcome back, ${user.email}!`);
    }
  }, [isAuthenticated, user, navigate, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(setError('')); // Clear any previous errors
    setLoading(true);

    try {
      await signIn(email, password);
      toast.success('Signed in successfully!');
      
      // Redirect back to the original page or default to home
      navigate(returnTo);
    } catch (error: any) {
      // Convert Firebase error to user-friendly message
      const userFriendlyMessage = getErrorMessage(error.code);
      dispatch(setError(userFriendlyMessage));
      
      toast.error(userFriendlyMessage || 'Failed to sign in');
      setDebugInfo({ 
        error: error.message, 
        code: error.code,
        userFriendlyMessage 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestAuth = async () => {
    try {
      const result = await testFirebaseAuth(email, password);
      setDebugInfo(result);
      if (result.success) {
        toast.success('Firebase authentication test successful!');
      } else {
        const userFriendlyMessage = result.error && typeof result.error === 'object' && 'code' in result.error 
          ? getErrorMessage((result.error as { code: string }).code) 
          : 'Authentication test failed';
        toast.error(userFriendlyMessage);
      }
    } catch (error: any) {
      setDebugInfo({ error: error.message });
      toast.error('Test failed: ' + error.message);
    }
  };

  const handleTestFirestore = async () => {
    try {
      const result = await testFirestoreWrite();
      setDebugInfo(result);
      if (result.success) {
        toast.success('Firestore write test successful!');
      } else {
        toast.error('Firestore write test failed');
      }
    } catch (error: any) {
      setDebugInfo({ error: error.message });
      toast.error('Test failed: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link to={`/signup${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`} className="font-medium text-blue-600 hover:text-blue-500">
                Create a new account
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Debug section */}
      <div className="mt-8 border-t pt-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Debug Options</h3>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handleTestAuth}
            className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
          >
            Test Auth
          </button>
          <button
            type="button"
            onClick={handleTestFirestore}
            className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
          >
            Test Firestore
          </button>
        </div>
        
        {debugInfo && (
          <div className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-32">
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}; 